"use client";

import { X } from "lucide-react";

export interface ColorPickerProps {
  /** Label for the color picker */
  label?: string;
  /** Current color value (hex format) */
  value: string;
  /** Callback when color changes */
  onChange: (value: string) => void;
  /** Default color to show when value is empty */
  defaultColor?: string;
  /** Show clear button */
  showClear?: boolean;
  /** Size variant */
  size?: "sm" | "md";
  /** Label width class */
  labelWidth?: string;
  /** Disabled state */
  disabled?: boolean;
}

/**
 * Reusable color picker component with color input and hex code text input
 */
const ColorPicker = ({
  label,
  value,
  onChange,
  defaultColor = "#000000",
  showClear = true,
  size = "sm",
  labelWidth = "w-12",
  disabled = false,
}: ColorPickerProps) => {
  const sizeClasses = {
    sm: {
      colorInput: "w-6 h-6",
      textInput: "w-20 px-1 py-0.5 text-xs",
      label: "text-xs",
      clearBtn: "w-3 h-3",
    },
    md: {
      colorInput: "w-10 h-10",
      textInput: "flex-1 px-2 py-1 text-sm",
      label: "text-sm",
      clearBtn: "w-4 h-4",
    },
  };

  const classes = sizeClasses[size];

  return (
    <div className="flex items-center gap-1">
      {label && (
        <label className={`${classes.label} text-gray-500 ${labelWidth}`}>{label}</label>
      )}
      <input
        type="color"
        value={value || defaultColor}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className={`${classes.colorInput} cursor-pointer border border-gray-300 rounded p-0 ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
      />
      <input
        type="text"
        value={value || ""}
        onChange={(e) => {
          const val = e.target.value;
          if (val === "" || /^#[0-9A-Fa-f]{0,6}$/.test(val)) {
            onChange(val);
          }
        }}
        placeholder={defaultColor}
        disabled={disabled}
        className={`${classes.textInput} border border-gray-300 rounded text-gray-900 placeholder-gray-400 ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
      />
      {showClear && value && !disabled && (
        <button
          onClick={() => onChange("")}
          className="text-gray-400 hover:text-red-500"
          title="Clear"
          type="button"
        >
          <X className={classes.clearBtn} />
        </button>
      )}
    </div>
  );
};

export default ColorPicker;

